import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import {
  AppShell,
  Avatar,
  Box,
  Burger,
  Group,
  Menu,
  ScrollArea,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import {
  IconCheck,
  IconChecklist,
  IconClipboardCheck,
  IconClipboardList,
  IconCalendarEvent,
  IconDashboard,
  IconLogout,
  IconSettings,
  IconUser,
  IconBug,
  IconMessageCircle,
} from '@tabler/icons-react';
import { Suspense, useEffect, useMemo, useState } from 'react';
import type { ComponentType } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { NotificationsPopover } from './components/NotificationsPopover';
import { InAppNotificationToastListener } from './components/InAppNotificationToastListener';
import { useInAppNotifications } from './hooks/useInAppNotifications';
import { FhirRealtimeProvider } from './hooks/useFhirRealtime';
import { AccessDenied } from './components/AccessDenied';
import { RequireClientRole } from './components/RequireClientRole';

import { HudClientSearch } from './components/HudClientSearch';
import { Loading } from './components/Loading';
import { RecentlyViewedNavSection } from './components/RecentlyViewedNavSection';
import { UnifyNavLink } from './components/UnifyNavLink';
import { ClientsProvider } from './contexts/ClientsContext';
import { RecentlyViewedClientsProvider } from './contexts/RecentlyViewedClientsContext';
import { usePermissions } from './contexts/PermissionContext';
import { usePortalSubject } from './contexts/PortalSubjectContext';
import {
  AiAssistantLayout,
  AiAssistantProvider,
  FreudLauncher,
} from './features/ai-assistant';
import { useApiInitialization } from './hooks';
import {
  clientPortalNavItemAllowed,
  defaultClientPortalHome,
  getEffectiveClientPortalPermissions,
  isClientPortalPathAllowed,
} from './lib/clientPortalNavPermissions';
import {
  type DisplayUserRole,
  getEffectiveDisplayUserRole,
} from './lib/displayUserRole';
import { collapseRolesForDisplay } from '@unify/fhir-model/role-display';
import { ROLE_LABELS } from './lib/rbacRoles';
import { profileAccountDisplayName } from './lib/profileAccountDisplayName';
import { useBugReport } from './hooks/useBugReport';
import { useProductFeedback } from './hooks/useProductFeedback';
import { BugReportModal } from './components/BugReportModal';
import { FeedbackModal } from './components/FeedbackModal';
import { USER_ROLE_STORAGE_KEY } from './lib/userProfileRouteAccess';
import { isAuthPath, isGuestJoinPath, shouldBlockRenderWhileLoading } from './lib/appRouteGate';
import { isInteractiveUserProfile } from './lib/isInteractiveMedplumProfile';
import { usePreferredDisplayTimeZone } from './hooks/usePreferredDisplayTimeZone';
import { ACCOUNT_STATUS_TAG_SYSTEM, PENDING_ACTIVATION_TAG_CODE } from '@unify/fhir-model/constants';

import { AuthCallback } from './pages/auth/AuthCallback';
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage';
import { SetPasswordPage } from './pages/auth/SetPasswordPage';
import { SignInPage } from './pages/auth/SignInPage';
import { ClientsScreen } from './pages/caseload/ClientsScreen';
import { AssessmentResponsePage } from './pages/clients/AssessmentResponsePage';
import { ChartAdminNoteRoute } from './pages/clients/ChartAdminNoteRoute';
import { ChartDocumentPage } from './pages/clients/ChartDocumentPage';
import { ClientChartPage } from './pages/clients/ClientChartPage';
import { ClientProfilePage } from './pages/clients/ClientProfilePage';
import { DischargeSummaryPage } from './pages/clients/DischargeSummaryPage';
import { EpisodeOfCarePage } from './pages/clients/EpisodeOfCarePage';
import { MedplumDirectoryUsersLayout } from './pages/settings/MedplumDirectoryUsersLayout';
import { SettingsPage } from './pages/settings/SettingsPage';

import { UserSettingsPage } from './pages/settings/UserSettingsPage';
import { TemplateEditorPage } from './pages/settings/template-library/TemplateEditorPage';
import { TasksPage } from './pages/tasks';
import { SupervisionDashboardPage, DocumentReviewPage } from './pages/supervision';
import { ProviderCalendarPage } from './pages/calendar';
import { HomeTilesPage } from './pages/dashboard-home';
import { AppointmentPage } from './pages/appointments';
import { ChimeCallPage } from './pages/session';
import { GuestJoinPage } from './pages/public/GuestJoinPage';
import {
  AssessmentFillPage,
  ClientPortalDashboardPage,
  ClientTelehealthJoinPage,
} from './pages/client-portal';
import { OnboardingHubPage } from './pages/onboarding/OnboardingHubPage';
import { OnboardingLayout } from './pages/onboarding/OnboardingLayout';
import { useOnboardingStatus } from './pages/onboarding/useOnboardingStatus';
import { DoneSection } from './pages/onboarding/sections/DoneSection';
import { PersonalInfoSection } from './pages/onboarding/sections/PersonalInfoSection';
import { AvailabilitySection } from './pages/onboarding/sections/AvailabilitySection';
import { ProfileSection } from './pages/onboarding/sections/ProfileSection';
import { LicensesEducationSection } from './pages/onboarding/sections/LicensesEducationSection';
import { AttestationSection } from './pages/onboarding/sections/AttestationSection';
import { isStaffFeaturePathVisible } from './config/roleVisibility';

/**
 * Navigation items for the sidebar
 */
interface NavItem {
  icon: ComponentType<{ size?: number | string; stroke?: number | string }>;
  label: string;
  href: string;
}

const userRoleOptions: Array<{ value: DisplayUserRole; label: string }> = [
  { value: 'Unify Admin', label: 'Unify Admin' },
  { value: 'Client', label: 'Client' },
  { value: 'Provider', label: 'Provider' },
  { value: 'Practice Owner', label: 'Practice Owner' },
  { value: 'Practice Admin', label: 'Practice Admin' },
];

const dashboardNavItems: NavItem[] = [
  { icon: IconDashboard, label: 'Dashboard', href: '/dashboard' },
];

const tasksNavItems: NavItem[] = [
  { icon: IconChecklist, label: 'Tasks', href: '/dashboard/tasks' },
];

const mainNavItems: NavItem[] = [...dashboardNavItems, ...tasksNavItems];

/**
 * Client portal section (client-facing screens).
 */
const clientPortalNavItems: NavItem[] = [
  { icon: IconDashboard, label: 'Dashboard', href: '/client-portal' },
];

/**
 * Provider section – complete nav items in display order
 */
const providerNavItems: NavItem[] = [
  { icon: IconDashboard, label: 'Dashboard', href: '/dashboard' },
  { icon: IconCalendarEvent, label: 'Calendar', href: '/dashboard/calendar' },
  { icon: IconClipboardList, label: 'Caseload', href: '/dashboard/caseload' },
  { icon: IconChecklist, label: 'Tasks', href: '/dashboard/tasks' },
  { icon: IconClipboardCheck, label: 'Supervision', href: '/dashboard/supervision' },
];

/**
 * Settings – visible to all roles
 */
const settingsNavItems: NavItem[] = [{ icon: IconSettings, label: 'Settings', href: '/dashboard/settings' }];

function isNavActive(href: string, pathname: string): boolean {
  // Prevent `/dashboard` from appearing "active" for all `/dashboard/*` routes,
  // which visually makes other items look nested under it.
  if (href === '/dashboard') {
    return pathname === '/dashboard' || pathname === '/dashboard/home';
  }
  // Same for client portal root: keep "Portal dashboard" active only on its exact route.
  if (href === '/client-portal') {
    return pathname === '/client-portal';
  }
  return pathname === href || pathname.startsWith(href + '/');
}

/** Redirect legacy /set-password/:id/:secret path-based URLs to /auth/set-password?id=…&secret=… */
function LegacySetPasswordRedirect(): JSX.Element {
  const { id, secret } = useParams<{ id: string; secret: string }>();
  const to = `/auth/set-password?id=${encodeURIComponent(id ?? '')}&secret=${encodeURIComponent(secret ?? '')}`;
  return <Navigate to={to} replace />;
}

/** Redirect /auth/setpassword (no hyphen) to /auth/set-password, preserving query params */
function SetPasswordAliasRedirect(): JSX.Element {
  const [searchParams] = useSearchParams();
  const qs = searchParams.toString();
  return <Navigate to={`/auth/set-password${qs ? `?${qs}` : ''}`} replace />;
}

/**
 * Main App component
 * Handles routing and layout based on authentication state
 */
export function App(): JSX.Element {
  return (
    <FhirRealtimeProvider>
      <InAppNotificationToastListener />
      <AppContent />
    </FhirRealtimeProvider>
  );
}

function AppContent(): JSX.Element | null {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  usePreferredDisplayTimeZone(profile);
  const navigate = useNavigate();
  const location = useLocation();
  const [opened, { toggle, close }] = useDisclosure();
  const [accountMenuOpened, setAccountMenuOpened] = useState(false);
  const bugReport = useBugReport();
  const productFeedback = useProductFeedback();
  // True when the viewport is narrower than Mantine's 'sm' breakpoint (~768px).
  // Used to adapt the header layout for mobile screens.
  const isMobileViewport = useMediaQuery('(max-width: 48em)') ?? false;

  // Medplum-backed permission state (authoritative in live mode)
  const rbac = usePermissions();

  // Portal subject: the one portal account for this login
  const portalSubject = usePortalSubject();
  const isViewingRelatedClient = portalSubject.activeAccount !== null && !portalSubject.activeAccount.isSelf;

  // Dev role toggle: authoritative in mock Medplum; in live mode overridden by PermissionContext when ready.
  const [devRole] = useState<DisplayUserRole>(() => {
    const saved = window.localStorage.getItem(USER_ROLE_STORAGE_KEY);
    if (saved && userRoleOptions.some((r) => r.value === saved)) {
      return saved as DisplayUserRole;
    }
    return 'Unify Admin';
  });

  const userRole: DisplayUserRole = getEffectiveDisplayUserRole(rbac, devRole);
  const activeRoleLabel = rbac.ready ? ROLE_LABELS[rbac.activeRole] : userRole;
  const switchableRoles = useMemo(
    () => collapseRolesForDisplay(rbac.availableRoles),
    [rbac.availableRoles],
  );

  // Clear pending-activation tag on first sign-in
  useEffect(() => {
    if (!profile?.id) return;
    const tags = profile.meta?.tag;
    const isPending = tags?.some(
      (t) => t.system === ACCOUNT_STATUS_TAG_SYSTEM && t.code === PENDING_ACTIVATION_TAG_CODE,
    );
    if (!isPending) return;
    const updated = {
      ...profile,
      meta: {
        ...profile.meta,
        tag: tags!.filter(
          (t) => !(t.system === ACCOUNT_STATUS_TAG_SYSTEM && t.code === PENDING_ACTIVATION_TAG_CODE),
        ),
      },
    };
    medplum.updateResource(updated).catch(() => { });
  }, [medplum, profile]);

  const effectivePortalPerms = useMemo(() => {
    return getEffectiveClientPortalPermissions(rbac.permissions, isViewingRelatedClient);
  }, [rbac.permissions, isViewingRelatedClient]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  /**
   * Client (Patient) users: sidebar only lists client-portal links, but routes like
   * /dashboard/caseload were still reachable by URL. Staff dashboard is not permission-
   * checked server-side in this SPA — Medplum AccessPolicy still limits FHIR data.
   */
  useEffect(() => {
    if (!profile || !rbac.ready || !rbac.isClient) {
      return;
    }
    const p = location.pathname;
    if (!p.startsWith('/dashboard')) {
      return;
    }
    const allowed =
      p === '/dashboard' ||
      p === '/dashboard/' ||
      p.startsWith('/dashboard/settings');
    if (!allowed) {
      navigate(defaultClientPortalHome(), { replace: true });
    }
  }, [profile, rbac.ready, rbac.isClient, location.pathname, navigate]);

  /** Client portal route guard: redirect from disallowed routes using effective permissions. */
  useEffect(() => {
    if (!rbac.ready || !rbac.isClient) {
      return;
    }
    const path = location.pathname;
    if (!path.startsWith('/client-portal')) {
      return;
    }
    if (isClientPortalPathAllowed(path, effectivePortalPerms)) {
      return;
    }
    navigate(defaultClientPortalHome(), { replace: true });
  }, [rbac.ready, rbac.isClient, effectivePortalPerms, location.pathname, navigate]);

  /** Role-gated staff settings routes stay off for other roles even via bookmark. */
  useEffect(() => {
    if (!rbac.ready || rbac.isClient) {
      return;
    }
    if (isStaffFeaturePathVisible(location.pathname, userRole, rbac.role)) {
      return;
    }
    navigate('/dashboard', { replace: true });
  }, [rbac.ready, rbac.isClient, location.pathname, navigate, userRole, rbac.role]);

  const visibleClientPortalNavItems = useMemo(() => {
    if (userRole !== 'Client' || !rbac.ready) {
      return clientPortalNavItems;
    }
    return clientPortalNavItems.filter((item) =>
      clientPortalNavItemAllowed(item.href, effectivePortalPerms),
    );
  }, [userRole, rbac.ready, effectivePortalPerms]);

  const inAppNotifications = useInAppNotifications();
  const [notifPopoverOpened, setNotifPopoverOpened] = useState(false);

  // Initialize API client for Spring Boot backend
  useApiInitialization();
  const { status: onboardingStatus, isLoading: onboardingStatusLoading } = useOnboardingStatus();

  // Auth routes (no shell). See `appRouteGate.ts`.
  const isAuthRoute = isAuthPath(location.pathname);
  const isInteractiveUser = isInteractiveUserProfile(profile);

  // Show nothing while Medplum is loading — but never block auth routes.
  // They don't depend on the authenticated profile, and gating them here would
  // unmount SignInPage on every profile refresh.
  if (shouldBlockRenderWhileLoading(medplum.isLoading(), location.pathname)) {
    return <Loading />;
  }

  // Signed guest join link — no login, no AppShell. All authorization happens in the
  // chime-guest-join bot from the token in the URL, so this must sit above the
  // interactive-user redirect: the guest's client_credentials profile is a
  // ClientApplication and would otherwise be bounced to /signin.
  if (isGuestJoinPath(location.pathname)) {
    return (
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/join/:code" element={<GuestJoinPage />} />
        </Routes>
      </Suspense>
    );
  }

  if (location.pathname === '/') {
    return <Navigate to={isInteractiveUser ? '/dashboard' : '/signin'} replace />;
  }

  const isOnboardingRoute = location.pathname.startsWith('/onboarding');
  if (!isInteractiveUser && !isAuthRoute) {
    return <Navigate to="/signin" replace />;
  }

  // Inconsistent role/profile state → safe access-denied view
  if (isInteractiveUser && rbac.error && !isAuthRoute) {
    return <AccessDenied />;
  }

  // Render onboarding routes (authenticated, minimal shell)
  if (isInteractiveUser && isOnboardingRoute) {
    if (!onboardingStatusLoading && onboardingStatus === 'completed') {
      return <Navigate to="/dashboard" replace />;
    }
    return (
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="onboarding" element={<OnboardingLayout />}>
            <Route index element={<OnboardingHubPage />} />
            <Route path="personal-info" element={<PersonalInfoSection />} />
            <Route path="availability" element={<AvailabilitySection />} />
            <Route path="profile" element={<ProfileSection />} />
            <Route path="licenses-education" element={<LicensesEducationSection />} />
            <Route path="attestation" element={<AttestationSection />} />
            <Route path="done" element={<DoneSection />} />
          </Route>
        </Routes>
      </Suspense>
    );
  }

  // Redirect incomplete onboarding to /onboarding for any authenticated app route
  if (
    isInteractiveUser &&
    !onboardingStatusLoading &&
    onboardingStatus &&
    onboardingStatus !== 'completed' &&
    !isAuthRoute &&
    !isOnboardingRoute
  ) {
    return <Navigate to="/onboarding" replace />;
  }

  // Render auth routes without app shell
  if (isAuthRoute) {
    return (
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/signin" element={<SignInPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/auth/set-password" element={<SetPasswordPage />} />
          <Route path="/auth/setpassword" element={<SetPasswordAliasRedirect />} />
          <Route path="/set-password/:id/:secret" element={<LegacySetPasswordRedirect />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
        </Routes>
      </Suspense>
    );
  }

  // Render telehealth session fullscreen (no AppShell chrome)
  if (location.pathname.match(/^\/client-portal\/session\//)) {
    return (
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route
            path="/client-portal/session/:appointmentId/join"
            element={
              <RequireClientRole>
                <ClientTelehealthJoinPage />
              </RequireClientRole>
            }
          />
        </Routes>
      </Suspense>
    );
  }

  if (location.pathname.match(/^\/dashboard\/session\//)) {
    return (
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/dashboard/session/:appointmentId" element={<ChimeCallPage />} />
        </Routes>
      </Suspense>
    );
  }

  const handleSignOut = (): void => {
    medplum.signOut();
    navigate('/signin');
  };

  return (
    <ClientsProvider>
      <RecentlyViewedClientsProvider>
      <AiAssistantProvider>
      <AppShell
        header={{ height: 60 }}
        navbar={{
          width: 250,
          breakpoint: 'sm',
          collapsed: { mobile: !opened },
        }}
        padding={location.pathname.startsWith('/dashboard/settings') ? 0 : 'md'}
        styles={{
          main: {
            backgroundColor: 'var(--unify-bg-warm)',
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
          },
          header: {
            padding: 0,
            borderBottom: '1px solid #9CA3AF',
          },
          navbar: {
            backgroundColor: 'var(--unify-bg-sidebar)',
            borderRight: '1px solid #9CA3AF',
          },
        }}
      >
        {/* Header: left brand strip + main bar (search, bell, user) */}
        <AppShell.Header>
          <Group h="100%" wrap="nowrap" gap={0}>
            {/* Left: sidebar header – on desktop matches sidebar width; on mobile collapses to burger-only */}
            <Group
              h="100%"
              px="md"
              gap="sm"
              wrap="nowrap"
              style={{
                backgroundColor: 'var(--unify-bg-sidebar)',
                borderRight: '1px solid #9CA3AF',
                minWidth: 0,
                width: isMobileViewport ? 'auto' : 250,
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
              <Text size="lg" fw={600} c="#111827" visibleFrom="sm">
                Unify Mental Health
              </Text>
            </Group>

            {/* Right: main header – white; center: search (desktop only); right: bell + user */}
            <Group
              h="100%"
              px="md"
              wrap="nowrap"
              gap={0}
              style={{ flex: 1, backgroundColor: 'var(--unify-bg-header)', minWidth: 0 }}
            >
              <Box style={{ flex: 1, minWidth: 0 }} aria-hidden />
              {/* Search is desktop-only: too wide for mobile header */}
              {userRole !== 'Client' && (
                <Box visibleFrom="sm">
                  <Group gap="xs" wrap="nowrap" align="center">
                    <HudClientSearch />
                  </Group>
                </Box>
              )}
              <Box
                style={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                }}
              >
                <Group gap="sm" wrap="nowrap">
                  <NotificationsPopover
                    opened={notifPopoverOpened}
                    onClose={() => setNotifPopoverOpened(false)}
                    onToggle={() => setNotifPopoverOpened((o) => !o)}
                    notifications={inAppNotifications}
                  />

                  {profile && (
                    <Menu
                      position="bottom-end"
                      width={240}
                      shadow="md"
                      opened={accountMenuOpened}
                      onChange={setAccountMenuOpened}
                    >
                      <Menu.Target>
                        <UnstyledButton aria-label="Account menu">
                          <Avatar
                            radius="xl"
                            size={32}
                            styles={{
                              root: {
                                width: 32,
                                height: 32,
                                minWidth: 32,
                                backgroundColor: 'var(--unify-bg-sidebar)',
                                color: '#374151',
                                border: '1px solid #E5E7EB',
                                fontSize: '0.75rem',
                                cursor: 'pointer',
                              },
                            }}
                          >
                            {(() => {
                              const n = profile.name?.[0];
                              const g = n?.given?.[0]?.[0];
                              const f = n?.family?.[0];
                              if (g && f) return `${g}${f}`.toUpperCase();
                              if (n?.text) return n.text.slice(0, 2).toUpperCase();
                              return 'U';
                            })()}
                          </Avatar>
                        </UnstyledButton>
                      </Menu.Target>
                      <Menu.Dropdown data-bug-report-exclude="">
                        <div style={{ padding: '8px 12px', borderBottom: '1px solid #E5E7EB' }}>
                          <Text fw={600} size="sm">
                            {profileAccountDisplayName(profile)}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {profile.telecom?.find((t) => t.system === 'email')?.value ?? '—'}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {activeRoleLabel}
                          </Text>
                        </div>
                        {userRole !== 'Client' && (
                          <Menu.Item
                            leftSection={<IconUser size={16} />}
                            onClick={() => navigate('/dashboard/settings/profile')}
                          >
                            User Profile
                          </Menu.Item>
                        )}
                        <Menu.Item
                          leftSection={<IconSettings size={16} />}
                          onClick={() => navigate('/dashboard/settings')}
                        >
                          Settings
                        </Menu.Item>
                        <Menu.Item
                          leftSection={<IconBug size={16} />}
                          onClick={() => {
                            setAccountMenuOpened(false);
                            void bugReport.startReport();
                          }}
                        >
                          Report a bug
                        </Menu.Item>
                        <Menu.Item
                          leftSection={<IconMessageCircle size={16} />}
                          onClick={() => {
                            setAccountMenuOpened(false);
                            productFeedback.open();
                          }}
                        >
                          Submit feedback
                        </Menu.Item>
                        {switchableRoles.length > 1 && (
                          <>
                            <Menu.Divider />
                            <Menu.Label>Switch Role</Menu.Label>
                            {switchableRoles.map((slug) => (
                              <Menu.Item
                                key={slug}
                                rightSection={
                                  rbac.activeRole === slug
                                    ? <IconCheck size={16} stroke={2} style={{ color: '#111827' }} />
                                    : undefined
                                }
                                onClick={() => {
                                  rbac.setActiveRole(slug);
                                  navigate('/dashboard');
                                }}
                              >
                                {ROLE_LABELS[slug]}
                              </Menu.Item>
                            ))}
                          </>
                        )}
                        <Menu.Item
                          leftSection={<IconLogout size={16} />}
                          color="red"
                          onClick={handleSignOut}
                        >
                          Logout
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  )}
                </Group>
              </Box>
            </Group>
          </Group>
        </AppShell.Header>

        {/* Sidebar Navigation */}
        <AppShell.Navbar p="md">
          <Box mb="sm" px="xs" pb="sm" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
            <Text size="sm" fw={700} tt="uppercase" c="gray.7" lts={0.5}>
              {activeRoleLabel}
            </Text>
          </Box>
          <AppShell.Section grow component={ScrollArea}>
            {(() => {
              const renderItems = (items: NavItem[]): JSX.Element => {
                return (
                  <>
                    {items.map((item) => (
                      <UnifyNavLink
                        key={item.href}
                        href={item.href}
                        label={item.label}
                        icon={<item.icon size={20} stroke={1.5} />}
                        active={isNavActive(item.href, location.pathname)}
                        onNavigate={() => {
                          navigate(item.href);
                          close();
                        }}
                      />
                    ))}
                  </>
                );
              };

              // Client sees only client portal navigation in sidebar (filtered by RBAC permissions).
              if (userRole === 'Client') {
                return renderItems(visibleClientPortalNavItems);
              }

              // Everyone else: Dashboard → Calendar → Caseload, then the rest.
              const calendarNavItem = (
                <UnifyNavLink
                  label="Calendar"
                  icon={<IconCalendarEvent size={20} stroke={1.5} />}
                  href="/dashboard/calendar"
                  active={location.pathname.startsWith('/dashboard/calendar')}
                  onNavigate={() => {
                    navigate('/dashboard/calendar');
                    close();
                  }}
                />
              );

              const caseloadNavItem = (
                <UnifyNavLink
                  label="Caseload"
                  icon={<IconClipboardList size={20} stroke={1.5} />}
                  href="/dashboard/caseload"
                  active={location.pathname.startsWith('/dashboard/caseload')}
                  onNavigate={() => {
                    navigate('/dashboard/caseload');
                    close();
                  }}
                />
              );

              const staffCoreNav = (
                <>
                  {renderItems(dashboardNavItems)}
                  {calendarNavItem}
                  {caseloadNavItem}
                  {renderItems(tasksNavItems)}
                </>
              );

              switch (userRole) {
                case 'Unify Admin':
                case 'Practice Owner':
                case 'Practice Admin':
                  return staffCoreNav;
                case 'Provider':
                  return renderItems(providerNavItems);
                default:
                  return renderItems(mainNavItems);
              }
            })()}
            {userRole !== 'Client' && (
              <RecentlyViewedNavSection
                onNavigate={(href) => {
                  navigate(href);
                  close();
                }}
              />
            )}
          </AppShell.Section>

          <AppShell.Section>
            {settingsNavItems.map((item) => (
              <UnifyNavLink
                key={item.href}
                href={item.href}
                label={item.label}
                icon={<item.icon size={20} stroke={1.5} />}
                active={isNavActive(item.href, location.pathname)}
                onNavigate={() => {
                  navigate(item.href);
                  close();
                }}
              />
            ))}
          </AppShell.Section>
        </AppShell.Navbar>

        {/* Main Content */}
        <AppShell.Main data-testid="app-shell-main">
          <AiAssistantLayout>
          <Box w="100%" style={{ minWidth: 0 }}>
            <Suspense fallback={<Loading />}>
              <Routes>
                <Route
                  path="/dashboard"
                  element={
                    userRole === 'Client' ? (
                      <Navigate to={defaultClientPortalHome()} replace />
                    ) : (
                      <HomeTilesPage />
                    )
                  }
                />
                <Route path="/dashboard/home" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard/caseload" element={<ClientsScreen />} />
                <Route path="/dashboard/clients/:id" element={<ClientChartPage />} />
                <Route path="/dashboard/clients/:id/profile" element={<ClientProfilePage />} />
                <Route path="/dashboard/clients/:id/episode-of-care/:episodeId" element={<EpisodeOfCarePage />} />
                <Route path="/dashboard/clients/:id/discharge-summary/:summaryId" element={<DischargeSummaryPage />} />
                <Route path="/dashboard/clients/:id/chart-document/new" element={<ChartDocumentPage />} />
                <Route path="/dashboard/clients/:id/chart-document/:responseId" element={<ChartDocumentPage />} />
                <Route path="/dashboard/clients/:id/chart/admin-note" element={<ChartAdminNoteRoute />} />
                <Route path="/dashboard/clients/:id/measures/assessment/:taskId" element={<AssessmentResponsePage />} />
                <Route path="/dashboard/tasks" element={<TasksPage />} />
                <Route
                  path="/dashboard/calendar"
                  element={userRole === 'Provider' || userRole === 'Unify Admin' || userRole === 'Practice Owner' || userRole === 'Practice Admin' ? <ProviderCalendarPage /> : <Navigate to="/dashboard" replace />}
                />
                <Route
                  path="/dashboard/appointments/:appointmentId"
                  element={
                    userRole === 'Client' ? <Navigate to="/dashboard" replace /> : <AppointmentPage />
                  }
                />
                <Route path="/dashboard/supervision" element={userRole === 'Provider' ? <SupervisionDashboardPage /> : <Navigate to="/dashboard" replace />} />
                <Route path="/dashboard/supervision/cosign-notes" element={userRole === 'Provider' ? <SupervisionDashboardPage /> : <Navigate to="/dashboard" replace />} />
                <Route path="/dashboard/supervision/unsigned-notes" element={userRole === 'Provider' ? <SupervisionDashboardPage /> : <Navigate to="/dashboard" replace />} />
                <Route path="/dashboard/supervision/missing-documents" element={userRole === 'Provider' ? <SupervisionDashboardPage /> : <Navigate to="/dashboard" replace />} />
                <Route path="/dashboard/supervision/review/:documentId" element={userRole === 'Provider' ? <DocumentReviewPage /> : <Navigate to="/dashboard" replace />} />
                <Route
                  path="/dashboard/compliance-audit"
                  element={<Navigate to="/dashboard/settings/compliance" replace />}
                />
                <Route path="/dashboard/help" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard/settings" element={<MedplumDirectoryUsersLayout />}>
                  <Route index element={<SettingsPage />} />
                  <Route
                    path="documentation/template/new"
                    element={userRole === 'Unify Admin' ? <TemplateEditorPage /> : <Navigate to="/dashboard/settings" replace />}
                  />
                  <Route
                    path="documentation/template/:templateId"
                    element={userRole === 'Unify Admin' ? <TemplateEditorPage /> : <Navigate to="/dashboard/settings" replace />}
                  />
                  <Route path="users/:id" element={<UserSettingsPage />} />
                  <Route path=":section" element={<SettingsPage />} />
                </Route>
                <Route
                  path="/client-portal"
                  element={
                    <RequireClientRole>
                      <ClientPortalDashboardPage />
                    </RequireClientRole>
                  }
                />
                <Route
                  path="/client-portal/assessments/:taskId"
                  element={
                    <RequireClientRole>
                      <AssessmentFillPage />
                    </RequireClientRole>
                  }
                />
                <Route path="/access-denied" element={<AccessDenied />} />
                <Route
                  path="*"
                  element={
                    <Navigate
                      to={userRole === 'Client' ? defaultClientPortalHome() : '/dashboard'}
                      replace
                    />
                  }
                />
              </Routes>
            </Suspense>
          </Box >
          </AiAssistantLayout>
        </AppShell.Main >
        <FreudLauncher visible={userRole !== 'Client'} />
      </AppShell>
      <BugReportModal
        opened={bugReport.opened}
        capturing={bugReport.capturing}
        screenshotBase64={bugReport.screenshotBase64}
        submitting={bugReport.submitting}
        onClose={bugReport.onClose}
        onSubmit={(values) => void bugReport.onSubmit(values)}
        onScreenshotChange={bugReport.onScreenshotChange}
      />
      <FeedbackModal
        opened={productFeedback.opened}
        submitting={productFeedback.submitting}
        onClose={productFeedback.onClose}
        onSubmit={(values) => void productFeedback.onSubmit(values)}
      />
      </AiAssistantProvider>
      </RecentlyViewedClientsProvider>
    </ClientsProvider>
  );
}